import { useEffect, useState } from "react";
import Topbar from "../layouts/Topbar";
import PageHeader from "../components/PageHeader";
import SubHeader from "../components/SubHeader";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tabs,
  Tab,
  Box,
  Grid,
  InputAdornment,
  IconButton,
} from "@mui/material";
import instance from "../../Utils/api";
import Image from "../../Utils/getImage";
import { toast } from "react-toastify";
import editIcon from "../../User/assets/icons/register-upload-edit.svg";
import overview_icon from "../../User/assets/icons/overview-icon.svg";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const Settings = () => {
  const [tab, setTab] = useState(0);

  const [data, setData] = useState({
    name: "",
    mobile: "",
    alt_mobile: "",
    role: "",
    email: "",
    address: "",
    profile_pic: null,
  });

  const [edit, setEdit] = useState(false);
  const [file, setFile] = useState("");

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const togglePassword = (key) => {
    setShowPassword((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const title = tab === 0
    ? { name: "Settings", subheading: "Update your profile", search: "", button: "" }
    : { name: "Change Password", subheading: "Update your account password", search: "", button: "" };

  // Fetch Admin Details
  const getAdmin = async () => {
    try {
      const response = await instance.get("/Admin/getAdminById");
      const respdata = response.data.data;
      setData({
        name: respdata.name,
        mobile: respdata.mobile,
        alt_mobile: respdata.alt_mobile,
        role: respdata.role,
        email: respdata.email,
        address: respdata.address,
        profile_pic: respdata.profile_pic,
      });
    } catch (err) {
      console.log(err);
    }
  };

  // Update Profile
  const updateAdmin = async () => {
    if (!data.name)
      return toast.error("Please enter your Name");

    // Primary Mobile
    if (!data.mobile || data.mobile.trim() === "")
      return toast.error("Mobile number is required");

    if (!/^\d{10}$/.test(data.mobile.trim()))
      return toast.error("Mobile number must be 10 digits");

    // Alternate Mobile (optional)
    if (
      data.alt_mobile &&
      data.alt_mobile.trim() !== "" &&
      !/^\d{10}$/.test(data.alt_mobile.trim())
    ) {
      return toast.error("Alternate mobile number must be 10 digits");
    }

    if (!data.address)
      return toast.error("Please enter your Address");

    try {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (key === "profile_pic") {
          if (file) formData.append("profile_pic", file);
        } else {
          formData.append(key, value);
        }
      });

      const response = await instance.put("/Admin/updateAdmin", formData);
      toast.success(response.data.message);
      setEdit(false);
    } catch (err) {
      toast.error(err.response?.data?.message);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleUpdatePassword = async () => {
    const { oldPassword, newPassword, confirmPassword } = passwordData;

    if (!oldPassword || !newPassword || !confirmPassword)
      return toast.error("All fields are required");

    if (newPassword !== confirmPassword)
      return toast.error("New password & confirm password do not match");

    try {
      const response = await instance.patch("/Admin/ResetPassword", {
        oldPassword,
        newPassword,
        confirmPassword,
      });

      toast.success(response.data.message);
      setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      toast.error(err.response?.data?.message);
    }
  };

  useEffect(() => {
    getAdmin();
  }, []);

  const handleUploadImage = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
      setData({ ...data, profile_pic: URL.createObjectURL(file) });
    }
  };

  return (
    <div>
      <Topbar />

      <section className="admin-catalogue">
        <PageHeader search="settings" showButton={false} files={false} title={title} />
        <SubHeader menu={false} filter={false} />

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            TabIndicatorProps={{
              style: { backgroundColor: "#E37B00" }
            }}
            sx={{
              "& .MuiTab-root": {
                textTransform: "capitalize",
                fontFamily: "Poppins, sans-serif",
                fontWeight: 500,
              },
              "& .Mui-selected": {
                color: "#E37B00 !important",
              },
            }}
          >
            <Tab label="Profile Settings" />
            <Tab label="Reset Password" />
          </Tabs>
        </Box>


        {tab === 0 && (
          <>
            <div className="profile-settings">
              <input
                type="file"
                accept="image/*"
                onChange={handleUploadImage}
                id="file-type"
                style={{ display: "none" }}
              />
              <label htmlFor="file-type">
                {file ? (
                  <img
                    className="profile-settings-img"
                    src={data?.profile_pic}
                    alt="profile"
                    width={86}
                    height={86}
                    onClick={() => setEdit(true)}
                  />
                ) : (
                  <Image
                    className="profile-settings-img"
                    src={
                      data?.profile_pic ? data?.profile_pic : overview_icon
                    }
                    alt="profile"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = overview_icon;
                    }}
                    width={86}
                    height={86}
                    onClick={() => setEdit(true)}
                  />
                )}
                <img
                  className="edit-icon"
                  src={editIcon}
                  alt="edit"
                  onClick={() => setEdit(true)}
                />
              </label>
            </div>

            <Grid container spacing={2}>
              {[
                { label: "Name", key: "name", type: "text", required: true },
                { label: "Mobile No", key: "mobile", type: "number", required: true },
                { label: "Alt Mobile No", key: "alt_mobile", type: "number", required: false },
              ].map((item, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <TextField
                    required={item.required}
                    label={item.label}
                    name={item.key}
                    type={item.type}
                    value={data[item.key] || ""}
                    onChange={(e) => setData({ ...data, [e.target.name]: e.target.value })}
                    fullWidth
                    disabled={!edit}
                  />
                </Grid>
              ))}

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select value={data.role} label="Role" disabled>
                    <MenuItem value="Admin">Admin</MenuItem>
                    <MenuItem value="JuniorDesigner">Junior Designer</MenuItem>
                    <MenuItem value="SeniorDesigner">Senior Designer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Email" value={data.email} fullWidth disabled required />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Address"
                  name="address"
                  value={data.address || ""}
                  onChange={(e) => setData({ ...data, [e.target.name]: e.target.value })}
                  fullWidth
                  disabled={!edit}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <div className="settings-btn">
                  {edit ? (
                    <>
                      <Button className="settings-cancel-btn" onClick={() => { setEdit(false); getAdmin(); }}>
                        Cancel
                      </Button>
                      <Button className="settings-update-btn" onClick={updateAdmin}>
                        Update
                      </Button>
                    </>
                  ) : (
                    <Button className="settings-update-btn" onClick={() => setEdit(true)}>
                      Edit
                    </Button>
                  )}
                </div>
              </Grid>
            </Grid>
          </>
        )}

        {tab === 1 && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {[
              { label: "Old Password", key: "oldPassword" },
              { label: "New Password", key: "newPassword" },
              { label: "Confirm Password", key: "confirmPassword" },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <TextField
                  type={showPassword[item.key] ? "text" : "password"}
                  label={item.label}
                  required
                  name={item.key}
                  value={passwordData[item.key]}
                  onChange={handlePasswordChange}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <IconButton onClick={() => togglePassword(item.key)} edge="start">
                          {showPassword[item.key] ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <Button
                className="reset-password-btn"
                onClick={handleUpdatePassword}
              >
                Reset Password
              </Button>

            </Grid>
          </Grid>
        )}
      </section>
    </div>
  );
};

export default Settings;
